import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { DeleteAccountDialog } from './DeleteAccountDialog';

/**
 * AccountCleanupCard — Org Settings panel for removing user accounts.
 *
 * Lists the org's users with a search filter and a per-row "🗑️ Delete
 * account" button that opens DeleteAccountDialog (clean / archive flow).
 * The current user's row and the org_owner's row both show a disabled
 * button with a tooltip — protecting against accidental self/owner
 * deletes — though the backend re-enforces this.
 */

const ROLE_LABELS = {
  parent: '👨‍👩‍👧 Parent',
  staff: '👨‍🏫 Staff',
  driver: '🚗 Driver',
  room_leader: '🎯 Room Leader',
  deputy_manager: '🛡️ Deputy',
  org_admin: '👑 Admin',
  org_owner: '👑 Owner',
};

export function AccountCleanupCard({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (!includeArchived && u.archived) return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (!q) return true;
      const haystack = `${u.email || ''} ${u.full_name || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [users, search, roleFilter, includeArchived]);

  const ROLE_OPTS = [
    { v: '',               label: 'All roles' },
    { v: 'parent',         label: '👨‍👩‍👧 Parents' },
    { v: 'staff',          label: '👨‍🏫 Staff' },
    { v: 'driver',         label: '🚗 Drivers' },
    { v: 'room_leader',    label: '🎯 Room Leaders' },
    { v: 'deputy_manager', label: '🛡️ Deputies' },
    { v: 'org_admin',      label: '👑 Admins' },
  ];

  return (
    <div className="card" style={{ padding: '1rem' }} data-testid="account-cleanup-card">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
        <div>
          <h3 style={{ margin: 0 }}>🗑️ Account Cleanup</h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
            Permanently remove user accounts so the email is freed for re-registration. Use <strong>Clean delete</strong> for wrong-role sign-ups; use <strong>Archive</strong> when the account already has children, bookings or shifts.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: '0.85rem' }}>
        <input
          type="search"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="account-cleanup-search"
          style={{ flex: '1 1 240px', fontSize: '0.85rem' }}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="account-cleanup-role-filter"
          style={{ fontSize: '0.85rem' }}
        >
          {ROLE_OPTS.map((o) => <option key={o.v || 'all'} value={o.v}>{o.label}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="account-cleanup-include-archived"
          />
          Include archived
        </label>
      </div>

      {error && <div className="error-message" style={{ marginTop: '0.75rem' }}>{error}</div>}
      {loading && <p style={{ marginTop: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Loading users…</p>}

      {!loading && (
        <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'hsl(var(--muted-foreground))', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Name</th>
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Email</th>
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Role</th>
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '0.85rem 0.4rem', color: 'hsl(var(--muted-foreground))' }}>
                    No users match these filters.
                  </td>
                </tr>
              )}
              {filtered.map((u) => {
                const isSelf = u._id === currentUser?._id;
                const isOwner = u.role === 'org_owner';
                const disabled = isSelf || isOwner;
                const tip = isSelf
                  ? 'You cannot delete your own account.'
                  : isOwner
                    ? 'The organization owner cannot be deleted.'
                    : '';
                return (
                  <tr key={u._id} style={u.archived ? { opacity: 0.55 } : undefined}>
                    <td style={{ padding: '0.35rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)', fontWeight: 600 }}>
                      {u.full_name || '(no name)'}
                      {u.archived && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }}>· archived</span>}
                    </td>
                    <td style={{ padding: '0.35rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)' }}>{u.email}</td>
                    <td style={{ padding: '0.35rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)' }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </td>
                    <td style={{ padding: '0.35rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)', textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setDeletingUserId(u._id)}
                        disabled={disabled}
                        title={tip}
                        data-testid={`account-cleanup-delete-${u._id}`}
                        style={{
                          fontSize: '0.78rem',
                          padding: '0.2rem 0.55rem',
                          color: disabled ? 'hsl(var(--muted-foreground))' : 'hsl(0 60% 45%)',
                        }}
                      >
                        🗑️ Delete account
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {deletingUserId && (
        <DeleteAccountDialog
          userId={deletingUserId}
          onClose={() => setDeletingUserId(null)}
          onDone={() => loadUsers()}
        />
      )}
    </div>
  );
}

export default AccountCleanupCard;
